/**
 * The execution-mode design is retained at
 * `current card execution-mode contract` for historical context.
 *
 * An intent card's `execution_mode` field decides:
 *   1. Whether the job runs in an AI worker (queue path) or in the browser
 *      (client path — no AI cost, no queue)
 *   2. Which AI capability to request (image_edit / image_generation /
 *      image_transparent / image_upscale / image_remove_bg)
 *   3. What dialog-v07 UI input mode to render
 *      (image / text / image+text / client_edit)
 *
 * Provider-level selection (fal vs Replicate vs Ideogram vs Recraft) is
 * NOT encoded here — that's AI_Module's job (H-2026-04-22-009). This
 * layer is capability-agnostic so provider swaps don't ripple into
 * dispatcher / consumer.
 *
 * 14 modes total: 9 AI + 5 client. Add a new mode = add one entry here,
 * the dialog-v07 + generation-consumer / client-handler registry pick up
 * the change automatically.
 */

export const DISPATCH_MATRIX = Object.freeze({
  // ── AI kind ──────────────────────────────────────────────────
  prompt: {
    kind: 'ai',
    capability: 'image_edit',
    transparent_default: false,
    ui_input_mode: 'image',
    notes: 'fal.ai flux-2/edit, legacy default — A-class image edit',
  },
  hybrid: {
    kind: 'ai',
    capability: 'image_edit',
    transparent_default: false,
    ui_input_mode: 'image',
    notes: 'fal.ai kontext, pro tier — A-class image edit',
  },
  pattern: {
    kind: 'ai',
    capability: 'image_generation',
    transparent_default: false,
    ui_input_mode: 'text',
    notes: 'Replicate seamless-texture — B1',
  },
  ideogram: {
    kind: 'ai',
    capability: 'image_generation',
    transparent_default: false,
    ui_input_mode: 'text',
    notes: 'fal-ai/ideogram/v3 — B2 / B3 / B4',
  },
  ideogram_transparent: {
    kind: 'ai',
    capability: 'image_transparent',
    transparent_default: true,
    ui_input_mode: 'text',
    notes: 'fal-ai/ideogram/v3/generate-transparent — B5 / C1-C4 / D2',
  },
  ideogram_remix: {
    kind: 'ai',
    capability: 'image_edit',
    transparent_default: false,
    ui_input_mode: 'image+text',
    notes: 'fal-ai/ideogram/v3/remix — D1 text+art combo',
  },
  recraft_svg: {
    kind: 'ai',
    capability: 'image_generation',
    transparent_default: true,
    ui_input_mode: 'text',
    notes: 'fal-ai/recraft-v3/svg — C2 vector logo',
  },
  upscale: {
    kind: 'ai',
    capability: 'image_upscale',
    transparent_default: false,
    ui_input_mode: 'image',
    notes: 'Replicate Real-ESRGAN / kie.ai — E1',
  },
  remove_bg: {
    kind: 'ai',
    capability: 'image_remove_bg',
    transparent_default: true,
    ui_input_mode: 'image',
    notes: 'BiRefNet Heavy (fal-ai/birefnet/v2) — E2',
  },

  // ── Client kind ──────────────────────────────────────────────
  client_canvas: {
    kind: 'client',
    client_handler: 'clientCanvasHandler',
    ui_input_mode: 'client_edit',
    notes: 'F1 Crop / Rotate / Resize, F6 Flip — fabric.js / canvas transform',
  },
  client_svg: {
    kind: 'client',
    client_handler: 'clientSvgHandler',
    ui_input_mode: 'client_edit',
    notes: 'F3 Frame / Border, F7 Shadow / Glow — SVG overlay',
  },
  client_text: {
    kind: 'client',
    client_handler: 'clientTextHandler',
    ui_input_mode: 'client_edit',
    notes: 'F2 Text Overlay — fabric.js text layer (NOT an AI text render)',
  },
  cached_template: {
    kind: 'client',
    client_handler: 'cachedTemplateHandler',
    ui_input_mode: 'client_edit',
    notes: 'F4 Mockup Preview — composite with templates/mockups/*.png',
  },
  client_pixel: {
    kind: 'client',
    client_handler: 'clientPixelHandler',
    ui_input_mode: 'client_edit',
    notes: 'F5 Color Picker — canvas pixel sampling',
  },
})

/**
 * Thrown when an intent card's execution_mode is not in DISPATCH_MATRIX.
 * Consumer code should treat this as a data integrity error (bad intent
 * card row) and route the job to `failed` with a human-readable reason.
 */
export class InvalidExecutionModeError extends Error {
  constructor(mode) {
    super(`Unknown execution_mode: ${JSON.stringify(mode)}`)
    this.name = 'InvalidExecutionModeError'
    this.code = 'INVALID_EXECUTION_MODE'
  }
}

/**
 * Provides the documented operation.
 * @param {string} mode
 * @returns {Readonly<Object>} matrix entry (config object)
 * @throws {InvalidExecutionModeError}
 */
export function getDispatchConfig(mode) {
  const cfg = DISPATCH_MATRIX[mode]
  if (!cfg) throw new InvalidExecutionModeError(mode)
  return cfg
}

/**
 * Provides the isAiMode operation.
 * @param mode The input value.
 * @returns The operation result.
 */
export function isAiMode(mode) {
  return DISPATCH_MATRIX[mode]?.kind === 'ai'
}

/**
 * Provides the isClientMode operation.
 * @param mode The input value.
 * @returns The operation result.
 */
export function isClientMode(mode) {
  return DISPATCH_MATRIX[mode]?.kind === 'client'
}

/**
 * Helper for dialog-v07: given an intent card's execution_mode, return
 * the UI input form shape the dialog should render. Falls back to the
 * `prompt` mode default ('image') for legacy cards whose execution_mode
 * is null / unset, matching the v0.6 behavior where all intents had an
 * image slot.
 *
 * @param {string | null | undefined} mode
 * @returns {'image' | 'text' | 'image+text' | 'client_edit'}
 */
export function getUiInputMode(mode) {
  if (!mode) return DISPATCH_MATRIX.prompt.ui_input_mode
  return DISPATCH_MATRIX[mode]?.ui_input_mode ?? DISPATCH_MATRIX.prompt.ui_input_mode
}
